import React from 'react';
import { Box, Typography, Checkbox } from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { FlagCardProps } from './types';
import { formatFlagName } from './utils';

const FlagCard: React.FC<FlagCardProps> = ({
  flag,
  isSelected,
  onToggleSelect,
  isLoading,
}) => {
  const isResolved = flag.status === 'resolved';
  const isBlocking = flag.category === 'BLOCKING';

  const getCardStyle = () => {
    const baseStyle = {
      position: 'relative' as const,
      overflow: 'hidden' as const,
      transition: 'all 0.2s ease-in-out',
      border: '1px solid',
      borderRadius: '4px',
      height: '100%',
    };

    if (isResolved) {
      return {
        ...baseStyle,
        backgroundColor: '#F0F9F0',
        borderColor: '#2E7D32',
      };
    }

    if (isBlocking) {
      return {
        ...baseStyle,
        backgroundColor: '#FFF5F5',
        borderColor: isSelected ? '#1976d2' : '#D32F2F',
        borderWidth: isSelected ? '2px' : '1px',
      };
    }

    return {
      ...baseStyle,
      backgroundColor: '#FFFBEB',
      borderColor: isSelected ? '#1976d2' : '#FB8C00',
      borderWidth: isSelected ? '2px' : '1px',
    };
  };

  const getIconColor = () => {
    if (isResolved) {
      return '#2E7D32';
    }

    if (isBlocking) {
      return '#D32F2F';
    }

    return '#FB8C00';
  };

  return (
    <Box sx={getCardStyle()}>
      {isBlocking && !isResolved && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '4px 8px',
            backgroundColor: '#FFE5E5',
            color: '#CC0000',
            borderBottomLeftRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.2px',
          }}
        >
          Bloqueante
        </Box>
      )}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative',
        }}
      >
        {!isResolved && (
          <Box sx={{ mr: 1 }}>
            <Checkbox
              checked={isSelected}
              onChange={() => onToggleSelect()}
              disabled={isLoading}
              color="primary"
              sx={{ p: 0.5 }}
            />
          </Box>
        )}
        <Box
          sx={{
            color: getIconColor(),
            marginRight: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isResolved ? (
            <CheckCircleIcon fontSize="small" />
          ) : isBlocking ? (
            <WarningIcon fontSize="small" />
          ) : (
            <InfoIcon fontSize="small" />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: isResolved
                ? '#2E7D32'
                : isBlocking
                ? '#D32F2F'
                : '#FB8C00',
            }}
          >
            {formatFlagName(flag.name)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            {flag.description}
          </Typography>
          {isResolved && (
            <Typography
              variant="caption"
              sx={{
                color: '#2E7D32',
                fontWeight: 500,
                mt: 1,
                display: 'block',
              }}
            >
              Resuelto
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FlagCard;
